import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
  type DragCancelEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  useMatchStore,
  BOARD_SLOTS,
  MAX_TAVERN_TIER,
} from '../../../stores/match';
import { useTransitionStore } from '../../../stores/transition';
import { allHeroes, type Hero } from '../../../game/data/heroes';
import { refreshCostForHero } from '../../../game/heroPowers';
import type { TavernFxEvent } from '../../../game/tavernFx';
import type { Card } from '../../../game/data/cards/types';
import { CardTile, cardAspectClass, type CardSize } from '../../../components/CardTile';
import { ScreenPlate } from '../../../components/ScreenPlate';
import { TurnTimer } from '../../../components/TurnTimer';
import { DiscoverModal } from '../../../components/DiscoverModal';
import { HeroPowerTargetingOverlay } from '../../../components/HeroPowerTargetingOverlay';
import { SynergyChipRow } from '../../../components/SynergyChip';
import { SynergyPanel } from '../../../components/SynergyPanel';
import { allTribeProgress } from '../../../components/synergyProgress';
import { ScoutingPanel } from '../../../components/ScoutingPanel';
import { sfx } from '../../../audio/sfx';
import { useEffectiveReducedMotion } from '../../../components/useReducedMotion';
import { useLayoutMode } from '../../../components/useLayoutMode';
import { HandRail, handRailClearance } from '../../../components/HandRail';
import { RailDots, BoardMinimap } from '../../../components/RailIndicator';
import type { RailHandle, RailProgress } from '../../../components/Rail';
import { ActionBar, type ActionDescriptor } from '../../../components/ActionBar';
import { useUiSelectionStore } from '../../../stores/uiSelection';
import { useCardDetailStore } from '../../../stores/cardDetail';
import { Rail, railItemStyle } from '../../../components/Rail';
import { Explain } from '../../../components/Explain';
import type { ExplainId } from '../../../content/explain';
import { EDGES } from '../../../theme/tokens';

// R6 — Tavern with drag-and-drop, turn timer, and discover modal.
//
// Reads:
//   hand, board, shop, gold, tavernTier, roundNumber, health,
//   shopFrozen, refreshCount, picks, phase, heroId, matchId,
//   opponentBoard, pendingTripleReward, discoverOptions,
//   turnTimerMs, turnTimerActive
//
// Actions:
//   buyFromShop, playFromHand(handIdx, slotIdx?),
//   sellFromBoard(slotIdx), reroll, toggleFreeze, upgradeTavern,
//   upgradeCost, endTurn, initTavern,
//   claimDiscoverReward(card), tick(deltaMs), reorderBoard(from,to)
//
// Drag gestures (dnd-kit PointerSensor with 8px activation distance —
// taps under 8px still fire onClick handlers, so the click-to-buy /
// click-to-play fallbacks remain functional):
//   1. Hand → empty board slot          → playFromHand(handIdx, slotIdx)
//   2. Hand → occupied board slot       → no-op
//   3. Board → empty board slot         → reorderBoard(from, to)
//   4. Board → occupied board slot      → reorderBoard(from, to) (swap)
//   5. Board → sell strip               → sellFromBoard(slotIdx)
//   6. Hand → sell strip                → no-op (hand isn't sellable;
//                                          sellFromBoard only acts on board)
//
// Selling is drag-ONLY (board card → sell strip). There is deliberately
// no tap-to-sell: a stray tap on a board unit must never sell it.

const TURN_DURATION_MS = 60_000;
const EMPTY_CARDS: Card[] = [];
const EMPTY_BOARD_FX = new Map<number, Extract<TavernFxEvent, { kind: 'board_buff' }>[]>();
const EMPTY_BOARD_FX_SLOT: Extract<TavernFxEvent, { kind: 'board_buff' }>[] = [];
const EMPTY_SHOP_FX = new Map<string, Extract<TavernFxEvent, { kind: 'shop_buff' }>[]>();
const EMPTY_CARD_FX: Extract<TavernFxEvent, { kind: 'shop_buff' }>[] = [];
const EMPTY_FX_ARRAY: TavernFxEvent[] = [];

type DragSource =
  | { kind: 'hand'; index: number }
  | { kind: 'board'; index: number };

type DropTarget =
  | { kind: 'board-slot'; index: number }
  | { kind: 'sell-strip' };

function parseDragId(id: string | number | undefined | null): DragSource | null {
  if (typeof id !== 'string') return null;
  if (id.startsWith('hand:')) {
    const idx = Number(id.slice(5));
    return Number.isInteger(idx) ? { kind: 'hand', index: idx } : null;
  }
  if (id.startsWith('board:')) {
    const idx = Number(id.slice(6));
    return Number.isInteger(idx) ? { kind: 'board', index: idx } : null;
  }
  return null;
}

function parseDropId(id: string | number | undefined | null): DropTarget | null {
  if (typeof id !== 'string') return null;
  if (id.startsWith('slot:')) {
    const idx = Number(id.slice(5));
    return Number.isInteger(idx) ? { kind: 'board-slot', index: idx } : null;
  }
  if (id === 'sell-strip') return { kind: 'sell-strip' };
  return null;
}

export function TavernPage() {
  const { matchId: urlMatchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const reducedMotion = useEffectiveReducedMotion();
  const { mode: layoutMode, isPhone } = useLayoutMode();
  // Content density for every card on the tavern screen. Landscape phones
  // can't spare the ~48px foot, so cards drop to icon mode. Portrait rails
  // render cards ~170-220px wide and need the 'lg' type scale to match —
  // the default scale keys off viewport breakpoints, which at 390px would
  // draw 9px rules text on a 220px card. Desktop keeps 'md'.
  const cardSize: CardSize =
    layoutMode === 'phoneLandscape'
      ? 'sm'
      : layoutMode === 'phonePortrait'
        ? 'lg'
        : 'md';
  // Rails are the tavern's layout primitive on phones: shop, board and hand
  // all scroll on one axis with the same gestures (swipe browses, drag acts,
  // tap reads). Desktop keeps static grids — there's nothing to scroll, and
  // a rail would add a gesture for no benefit.
  const boardRailed = isPhone;
  const shopRailed = isPhone;
  // Rail cards size from the band's HEIGHT, so the band needs an upper bound.
  // Left unbounded (flex-1 in tall portrait) the cards stretched to fill the
  // screen. vh-based so short landscape shrinks, with a hard ceiling so
  // cards don't blow up on tablets.
  // Portrait gets a far taller budget — at 390×844 the height is there to
  // spend, and spending it on card size was the whole point of going
  // vertical. Landscape keeps the tight bands.
  const portrait = layoutMode === 'phonePortrait';

  const shopZoneStyle: React.CSSProperties = useMemo(
    () =>
      portrait
        ? { flex: '1 1 0', minHeight: '150px' }
        : { height: 'clamp(84px, 21vh, 176px)' },
    [portrait],
  );
  const boardZoneStyle: React.CSSProperties = useMemo(
    () =>
      portrait
        ? { flex: '1 1 0', minHeight: '130px' }
        : { height: 'clamp(84px, 20vh, 168px)' },
    [portrait],
  );

  const shopCardCap: React.CSSProperties | undefined = useMemo(
    () => (portrait ? { maxHeight: '225px' } : undefined),
    [portrait],
  );
  const boardCardCap: React.CSSProperties | undefined = useMemo(
    () => (portrait ? { maxHeight: '165px' } : undefined),
    [portrait],
  );

  // Individual, granular selectors — never subscribe to the whole store.
  const storedMatchId = useMatchStore((s) => s.matchId);
  const heroId = useMatchStore((s) => s.heroId);
  const picks = useMatchStore((s) => s.picks);
  const phase = useMatchStore((s) => s.phase);
  const initTavern = useMatchStore((s) => s.initTavern);

  const roundNumber = useMatchStore((s) => s.roundNumber);
  const health = useMatchStore((s) => s.health);
  const gold = useMatchStore((s) => s.gold);
  const tavernTier = useMatchStore((s) => s.tavernTier);
  const hand = useMatchStore((s) => s.hand);
  const board = useMatchStore((s) => s.board);
  const shop = useMatchStore((s) => s.shop);
  const shopFrozen = useMatchStore((s) => s.shopFrozen);
  const refreshCount = useMatchStore((s) => s.refreshCount);
  const freeRefreshCredits = useMatchStore((s) => s.freeRefreshCredits);
  const opponentBoardLength = useMatchStore((s) => s.opponentBoard.length);
  const opponents = useMatchStore((s) => s.opponents);

  const pendingTripleReward = useMatchStore((s) => s.pendingTripleReward);
  const discoverOptions = useMatchStore((s) => s.discoverOptions ?? EMPTY_CARDS);
  const pendingAbilityDiscover = useMatchStore((s) => s.pendingAbilityDiscover);
  const abilityDiscoverOptions = useMatchStore((s) => s.abilityDiscoverOptions ?? EMPTY_CARDS);
  const claimAbilityDiscover = useMatchStore((s) => s.claimAbilityDiscover);
  const turnTimerMs = useMatchStore((s) => s.turnTimerMs ?? TURN_DURATION_MS);
  const turnTimerActive = useMatchStore((s) => s.turnTimerActive ?? false);
  const claimDiscoverReward = useMatchStore((s) => s.claimDiscoverReward);
  const tick = useMatchStore((s) => s.tick);
  const reorderBoard = useMatchStore((s) => s.reorderBoard);

  const heroPowerStateRaw = useMatchStore((s) => s.heroPowerState);
  const heroPowerAvailable = typeof heroPowerStateRaw !== 'undefined';
  const heroPowerState: 'idle' | 'targeting' | 'discovering' | 'used' =
    heroPowerStateRaw ?? 'idle';
  const heroPowerTargetType = useMatchStore((s) => s.heroPowerTargetType ?? null);
  const heroPowerDiscoverOptions = useMatchStore((s) => s.heroPowerDiscoverOptions ?? EMPTY_CARDS);
  const heroPowerUsedThisTurn = useMatchStore((s) => s.heroPowerUsedThisTurn ?? false);
  const activateHeroPower = useMatchStore((s) => s.activateHeroPower);
  const selectHeroPowerTarget = useMatchStore((s) => s.selectHeroPowerTarget);
  const claimHeroPowerDiscover = useMatchStore((s) => s.claimHeroPowerDiscover);
  const cancelHeroPower = useMatchStore((s) => s.cancelHeroPower);

  const buyFromShop = useMatchStore((s) => s.buyFromShop);
  const playFromHand = useMatchStore((s) => s.playFromHand);
  const sellFromBoard = useMatchStore((s) => s.sellFromBoard);
  const reroll = useMatchStore((s) => s.reroll);
  const toggleFreeze = useMatchStore((s) => s.toggleFreeze);
  const upgradeTavern = useMatchStore((s) => s.upgradeTavern);
  const upgradeCost = useMatchStore((s) => s.upgradeCost);
  const endTurn = useMatchStore((s) => s.endTurn);

  // Effective reroll cost — Don Promotor's first refresh each turn is
  // free, and board units (Pasador de Backstage, Afinador de Beats) can
  // grant free-refresh credits on top. Every gate below (button, keyboard,
  // handler) uses this instead of a hardcoded 1g so a free refresh stays
  // reachable at 0 gold.
  const rerollCost = useMemo(() => {
    const heroCost = refreshCostForHero(
      allHeroes.find((h) => h.id === heroId) ?? null,
      1,
      refreshCount,
    );
    return heroCost > 0 && freeRefreshCredits > 0 ? 0 : heroCost;
  }, [heroId, refreshCount, freeRefreshCredits]);

  // --- Tavern FX playback -------------------------------------------
  // Subscribe to tavernFx updates directly without binding a whole-component
  // re-render on each commit. We play each new event once (slot flash + floating
  // "+X/+Y") and drop it from the active list after ~1s.
  const [activeFx, setActiveFx] = useState<TavernFxEvent[]>([]);
  const lastFxIdRef = useRef(0);

  useEffect(() => {
    // Initial check
    const initialFx = useMatchStore.getState().tavernFx ?? [];
    if (initialFx.length > 0) {
      const fresh = initialFx.filter((e) => e.id > lastFxIdRef.current);
      if (fresh.length > 0) {
        lastFxIdRef.current = fresh[fresh.length - 1].id;
        setActiveFx((cur) => [...cur, ...fresh]);
        const ids = new Set(fresh.map((e) => e.id));
        window.setTimeout(() => {
          setActiveFx((cur) => cur.filter((e) => !ids.has(e.id)));
        }, 1100);
      }
    }

    const unsubscribe = useMatchStore.subscribe((state, prevState) => {
      const fx = state.tavernFx;
      if (!fx || fx === prevState?.tavernFx) return;
      const fresh = fx.filter((e) => e.id > lastFxIdRef.current);
      if (fresh.length === 0) return;
      lastFxIdRef.current = fresh[fresh.length - 1].id;
      setActiveFx((cur) => [...cur, ...fresh]);
      const ids = new Set(fresh.map((e) => e.id));
      window.setTimeout(() => {
        setActiveFx((cur) => cur.filter((e) => !ids.has(e.id)));
      }, 1100);
    });

    return unsubscribe;
  }, []);

  const boardFxBySlot = useMemo(() => {
    if (activeFx.length === 0) return EMPTY_BOARD_FX;
    const m = new Map<number, Extract<TavernFxEvent, { kind: 'board_buff' }>[]>();
    for (const e of activeFx) {
      if (e.kind !== 'board_buff') continue;
      const list = m.get(e.slot);
      if (list) list.push(e);
      else m.set(e.slot, [e]);
    }
    return m;
  }, [activeFx]);

  const shopFxByInstance = useMemo(() => {
    if (activeFx.length === 0) return EMPTY_SHOP_FX;
    const m = new Map<string, Extract<TavernFxEvent, { kind: 'shop_buff' }>[]>();
    for (const e of activeFx) {
      if (e.kind !== 'shop_buff') continue;
      const list = m.get(e.instanceId);
      if (list) list.push(e);
      else m.set(e.instanceId, [e]);
    }
    return m;
  }, [activeFx]);

  const healFx = useMemo(() => {
    if (activeFx.length === 0) return EMPTY_FX_ARRAY as Extract<TavernFxEvent, { kind: 'hero_heal' }>[];
    return activeFx.filter((e): e is Extract<TavernFxEvent, { kind: 'hero_heal' }> => e.kind === 'hero_heal');
  }, [activeFx]);

  const goldFx = useMemo(() => {
    if (activeFx.length === 0) return EMPTY_FX_ARRAY as Extract<TavernFxEvent, { kind: 'gold_gain' }>[];
    return activeFx.filter((e): e is Extract<TavernFxEvent, { kind: 'gold_gain' }> => e.kind === 'gold_gain');
  }, [activeFx]);

  // Initialize the tavern on first mount (when shop is empty).
  // Re-initialize is gated on shop.length === 0 so navigating back
  // and forth doesn't wipe progress.
  useEffect(() => {
    if (phase === 'tavern' && shop.length === 0 && picks.length > 0) {
      initTavern();
    }
  }, [phase, shop.length, picks.length, initTavern]);

  // Drive the turn-timer tick. Pause while:
  //   - the discover modal is open (pendingTripleReward !== null)
  //   - the hero-power UI is open (targeting or discovering)
  //   - the timer is inactive (combat phase, etc.)
  //   - the app/tab is backgrounded (document.hidden) — prevents silent
  //     round commits on mobile when the player pockets the phone
  const heroPowerOpen =
    heroPowerState === 'targeting' || heroPowerState === 'discovering';
  // Battlecry Discover queues BEHIND the triple reward and the hero power —
  // one modal on screen at a time — but blocks the tavern just the same.
  const abilityDiscoverOpen =
    pendingAbilityDiscover !== null &&
    abilityDiscoverOptions.length > 0 &&
    pendingTripleReward === null &&
    !heroPowerOpen;
  // Any modal that must be resolved before the tavern accepts input again.
  const tavernBlocked =
    pendingTripleReward !== null || pendingAbilityDiscover !== null;
  useEffect(() => {
    if (!tick) return;
    if (!turnTimerActive) return;
    if (tavernBlocked) return;
    if (heroPowerOpen) return;

    let intervalId: number | null = null;
    const startInterval = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(() => tick(100), 100);
    };
    const stopInterval = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const handleVisibility = () => {
      if (document.hidden) stopInterval();
      else startInterval();
    };

    if (!document.hidden) startInterval();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stopInterval();
    };
  }, [tick, turnTimerActive, tavernBlocked, heroPowerOpen]);

  // Hand off to combat as soon as endTurn populates an opponent board.
  //
  // This single watcher handles BOTH manual end-turn and timer auto-end, which
  // is precisely why the FIGHT intro hangs off it rather than off the End Turn
  // button: a player who lets the clock run out has to get the same moment as
  // one who pressed the button. The splice owns the navigation from here — see
  // components/transition/SpliceRenderer.tsx.
  useEffect(() => {
    if (phase !== 'tavern') return;
    if (!urlMatchId) return;
    if (opponentBoardLength === 0) return;

    const go = () => navigate(`/game/${urlMatchId}/combat`);

    // Read non-reactively: these are only needed at the instant of hand-off,
    // and subscribing to them would re-run this watcher during normal play.
    const s = useMatchStore.getState();
    const you = allHeroes.find((h) => h.id === s.heroId)?.name ?? 'Your crew';
    const them = s.opponents[s.currentOpponentIdx]?.name ?? 'The wall';

    const started = useTransitionStore.getState().start({
      kind: 'splice',
      commit: go,
      payload: {
        you,
        them,
        // Round 1 gets the full splice; from round 2 the ornament drops and the
        // sequence compresses, so a moment the player sees a dozen times a
        // match stays punctuation. See theme/spliceTimeline.ts.
        familiar: s.roundNumber > 1 ? '1' : '0',
      },
    });

    // If some other sequence is already in flight, go straight there rather
    // than stranding the player in a tavern whose turn has already ended.
    if (!started) go();
  }, [opponentBoardLength, phase, urlMatchId, navigate]);

  // Triple-proximity glow set. If hand+board contains 2+ of a card,
  // matching shop cards get a sodium ring so the player sees the third
  // before buying. Recomputed only on hand/board change.
  // Copies of each card id the player already holds (hand + board). The set of
  // "2 or more" ids drives the existing shop glow; the full count also feeds
  // CardTile's triple-progress badge, which turns a memory task into a glance.
  const copiesById = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of hand) counts.set(c.id, (counts.get(c.id) ?? 0) + 1);
    for (const c of board) if (c) counts.set(c.id, (counts.get(c.id) ?? 0) + 1);
    return counts;
  }, [hand, board]);

  const tripleCandidateIds = useMemo(() => {
    const result = new Set<string>();
    for (const [id, n] of copiesById) if (n >= 2) result.add(id);
    return result;
  }, [copiesById]);

  // Aggregate board ATK / HP so the player gets a HS-Battlegrounds-style
  // power readout without having to mentally sum cards.
  // Per-tribe standing: fielded count plus what hand and shop could add.
  // Hand and shop are potential only — never folded into the fielded number
  // the engine actually reads (see synergyProgress).
  const tribeProgress = useMemo(
    () => allTribeProgress(board, hand, shop),
    [board, hand, shop],
  );

  const boardPower = useMemo(() => {
    let atk = 0;
    let hp = 0;
    for (const c of board) {
      if (c) {
        atk += c.attack;
        hp += c.health;
      }
    }
    return { atk, hp };
  }, [board]);

  // dnd-kit setup — single PointerSensor handles both mouse and touch.
  // distance: 8 lets taps through (so click handlers still buy / play /
  // sell for users on legacy test scripts).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const [activeDrag, setActiveDrag] = useState<DragSource | null>(null);

  // --- Tap-select grammar ---
  // Tap any card → select it → the ActionBar surfaces the zone's verbs.
  // Drag is the equivalent fast path and always clears the selection first.
  const selection = useUiSelectionStore((s) => s.selection);
  const moveArmed = useUiSelectionStore((s) => s.moveArmed);
  const selectCard = useUiSelectionStore((s) => s.select);
  const armMove = useUiSelectionStore((s) => s.armMove);
  const clearSelection = useUiSelectionStore((s) => s.clear);
  const setDragging = useUiSelectionStore((s) => s.setDragging);

  // Selection stores indexes, but hand/board/shop arrays shift under it
  // (buys, plays, sells, rerolls). Validate the remembered instanceId
  // against what now lives at that index and drop stale selections.
  useEffect(() => {
    if (!selection) return;
    const live =
      selection.zone === 'shop'
        ? shop[selection.index]
        : selection.zone === 'hand'
          ? hand[selection.index]
          : board[selection.index];
    const liveId = live ? (live.instanceId ?? live.id) : undefined;
    if (liveId !== selection.instanceId) clearSelection();
  }, [selection, shop, hand, board, clearSelection]);

  // Selection yields to every exclusive-focus state: hero-power targeting,
  // discover, and the end of the turn.
  useEffect(() => {
    if (heroPowerState !== 'idle') clearSelection();
  }, [heroPowerState, clearSelection]);
  useEffect(() => {
    if (phase !== 'tavern') clearSelection();
  }, [phase, clearSelection]);

  // Escape clears; outside taps clear. Card surfaces and the ActionBar mark
  // themselves data-selkeep, so any pointerdown whose path lacks the marker
  // is "dead space". Runs on pointerdown (before click) — selecting another
  // card still works because its own click handler fires afterwards.
  useEffect(() => {
    if (!selection) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearSelection();
    };
    const onDown = (e: PointerEvent) => {
      const keep = e
        .composedPath()
        .some(
          (el) =>
            el instanceof HTMLElement && el.dataset.selkeep !== undefined,
        );
      if (!keep) clearSelection();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [selection, clearSelection]);

  const hero = useMemo(() => allHeroes.find((h) => h.id === heroId), [heroId]);
  const nextTierCost = upgradeCost();
  const canUpgrade = tavernTier < MAX_TAVERN_TIER && gold >= nextTierCost;
  const handIsFull = hand.length >= 10;
  const boardHasRoom = board.some((s) => s === null);

  // Hero-power rail button derivation. `isActive` here refers to the
  // game-data field on Hero (the hero is in rotation) — separately from
  // whether its power is active vs passive (we read powerType for that).
  const heroPowerIsActive = hero
    ? hero.powerType.startsWith('active_')
    : false;
  const heroPowerCost = hero?.cost ?? 0;
  const heroPowerCanAfford = gold >= heroPowerCost;
  // The label is the hero's name suffixed with "Power" — keeps the
  // button readable across all 21 heroes without crowding the rail.
  const heroPowerLabel = hero ? `${hero.name} Power` : 'Hero Power';

  // Targeting flips the board / shop into target-pick mode. While
  // open we suppress dnd-kit drag wiring so a tap on a unit/shop card
  // routes to selectHeroPowerTarget instead of buy/play/sell.
  const targetingUnits =
    heroPowerState === 'targeting' && heroPowerTargetType === 'unit';
  const targetingShop =
    heroPowerState === 'targeting' && heroPowerTargetType === 'shop';

  // The sell strip should appear only while a board card is being
  // dragged (selling from hand isn't supported by the current store).
  const sellStripVisible = activeDrag?.kind === 'board';

  // Card currently being dragged — passed to the drag overlay.
  const draggedCard: Card | null = useMemo(() => {
    if (!activeDrag) return null;
    if (activeDrag.kind === 'hand') return hand[activeDrag.index] ?? null;
    return board[activeDrag.index] ?? null;
  }, [activeDrag, hand, board]);

  // Sell-feedback popup. Captures the gold delta when sellFromBoard is
  // invoked so the player gets a "+1g" floater above the Gold chip.
  // ID counter forces remount per sell so consecutive sells each animate.
  const [sellGainPopup, setSellGainPopup] = useState<{ id: number; amount: number } | null>(null);
  const handleSellFromBoard = useCallback((slotIdx: number) => {
    const goldBefore = useMatchStore.getState().gold;
    sellFromBoard(slotIdx);
    const goldAfter = useMatchStore.getState().gold;
    const delta = goldAfter - goldBefore;
    if (delta > 0) {
      setSellGainPopup({ id: Date.now(), amount: delta });
    }
  }, [sellFromBoard]);

  const handleDismissSellGain = useCallback(() => {
    setSellGainPopup(null);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    clearSelection();
    setDragging(true);
    setActiveDrag(parseDragId(event.active.id));
  }, [clearSelection, setDragging]);

  const handleDragCancel = useCallback((_event: DragCancelEvent) => {
    setDragging(false);
    setActiveDrag(null);
  }, [setDragging]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const source = parseDragId(event.active.id);
    const target = parseDropId(event.over?.id ?? null);
    setDragging(false);
    setActiveDrag(null);
    if (!source || !target) return;

    if (target.kind === 'sell-strip') {
      if (source.kind === 'board') {
        handleSellFromBoard(source.index);
      }
      return;
    }

    const targetIdx = target.index;
    if (source.kind === 'hand') {
      const currentBoard = useMatchStore.getState().board;
      if (currentBoard[targetIdx] === null) {
        playFromHand(source.index, targetIdx);
      }
      return;
    }
    if (source.kind === 'board') {
      if (source.index === targetIdx) return;
      if (reorderBoard) {
        reorderBoard(source.index, targetIdx);
      }
    }
  }, [setDragging, handleSellFromBoard, playFromHand, reorderBoard]);

  // End Turn with paint-yield so the tap feels instant and doesn't block the frame.
  const isEndingTurnRef = useRef(false);
  const handleEndTurn = useCallback(() => {
    if (isEndingTurnRef.current) return;
    isEndingTurnRef.current = true;
    clearSelection();
    sfx.success();
    // Yield to the browser frame first so the pressed state renders immediately,
    // then run the turn simulation on the subsequent frame.
    requestAnimationFrame(() => {
      setTimeout(() => {
        endTurn();
      }, 0);
    });
  }, [clearSelection, endTurn]);

  // SFX-wrapped action handlers — stable callbacks.
  const handleBuy = useCallback((instanceId: string) => {
    sfx.buy();
    buyFromShop(instanceId);
  }, [buyFromShop]);

  // Shop affordability, surfaced on the tile BEFORE the first tap. Deliberately
  // the same expression as the Buy verb's `disabled` below (gold < 3 ||
  // handIsFull) — buyFromShop hardcodes a cost of 3 and does not apply hero
  // shop discounts, so 3 is what the game actually charges. If that ever gains
  // a discount, both this and the verb must change together.
  const canBuyShopCard = gold >= 3 && !handIsFull;

  // Second tap on an already-selected shop card buys it, halving the pointer
  // travel of the most frequent verb in the screen. Mirrors the Buy verb's
  // press handler, including clearing the selection afterwards.
  const handleConfirmBuy = useCallback(
    (card: Card) => {
      if (!canBuyShopCard) {
        sfx.error();
        return;
      }
      handleBuy(card.instanceId!);
      clearSelection();
    },
    [canBuyShopCard, handleBuy, clearSelection],
  );

  const handleReroll = useCallback(() => {
    if (gold < rerollCost) {
      sfx.error();
      return;
    }
    sfx.refresh();
    reroll();
  }, [gold, rerollCost, reroll]);

  const handleToggleFreeze = useCallback(() => {
    sfx.click();
    toggleFreeze();
  }, [toggleFreeze]);

  const handleUpgrade = useCallback(() => {
    if (!canUpgrade) {
      sfx.error();
      return;
    }
    sfx.tierUp();
    upgradeTavern();
  }, [canUpgrade, upgradeTavern]);

  const handleActivateHeroPower = useCallback(() => {
    if (!activateHeroPower) return;
    sfx.heroPower();
    activateHeroPower();
  }, [activateHeroPower]);

  const handleCancelHeroPower = useCallback(() => {
    cancelHeroPower?.();
  }, [cancelHeroPower]);

  // Keyboard shortcuts — Space=End Turn, R=Reroll, F=Freeze, U=Upgrade,
  // H=Hero Power. Gated on modal/overlay states so they don't fire while
  // the player is mid-decision in a discover/targeting flow.
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      const tgt = e.target as HTMLElement | null;
      if (
        tgt?.tagName === 'INPUT' ||
        tgt?.tagName === 'TEXTAREA' ||
        tgt?.isContentEditable
      ) {
        return;
      }
      if (phase !== 'tavern') return;
      if (tavernBlocked) return;
      if (heroPowerOpen) return;

      const k = e.key.toLowerCase();
      switch (k) {
        case ' ':
          e.preventDefault();
          handleEndTurn();
          break;
        case 'r':
          handleReroll();
          break;
        case 'f':
          handleToggleFreeze();
          break;
        case 'u':
          handleUpgrade();
          break;
        case 'h':
          if (
            heroPowerAvailable &&
            heroPowerState === 'idle' &&
            !heroPowerUsedThisTurn &&
            activateHeroPower
          ) {
            handleActivateHeroPower();
          }
          break;
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [
    phase,
    tavernBlocked,
    heroPowerOpen,
    handleEndTurn,
    handleReroll,
    handleToggleFreeze,
    handleUpgrade,
    handleActivateHeroPower,
    heroPowerAvailable,
    heroPowerState,
    heroPowerUsedThisTurn,
    activateHeroPower,
  ]);

  // Triple-formed jingle + flourish. Fires when pendingTripleReward
  // transitions from null → set. The flourish overlay covers the tavern
  // for ~700ms before the DiscoverModal opens so the moment lands
  // visually instead of jumping straight into a reward picker. Reduced
  // motion skips the overlay entirely.
  const tripleTier = pendingTripleReward?.tier ?? null;
  const [tripleFlourishing, setTripleFlourishing] = useState(false);
  useEffect(() => {
    if (tripleTier === null) return;
    sfx.triple();
    if (reducedMotion) return;
    setTripleFlourishing(true);
    const id = window.setTimeout(() => setTripleFlourishing(false), 700);
    return () => window.clearTimeout(id);
  }, [tripleTier, reducedMotion]);

  // Rail indicators — visible-window state reported by the shop and board
  // rails, driving the dots / minimap strips so off-screen cards are never
  // invisible-by-omission (scrollbars are hidden app-wide).
  const shopRailRef = useRef<RailHandle | null>(null);
  const boardRailRef = useRef<RailHandle | null>(null);
  const [shopProgress, setShopProgress] = useState<RailProgress | null>(null);
  const [boardProgress, setBoardProgress] = useState<RailProgress | null>(null);

  // Empty board slots become tap targets while a hand card is selected
  // (placement) or Move is armed (reorder destination).
  const slotTapArmed =
    (selection?.zone === 'hand' && boardHasRoom) || moveArmed;

  const handleSelectBoard = useCallback((index: number) => {
    sfx.click();
    const card = useMatchStore.getState().board[index];
    selectCard({
      zone: 'board',
      index,
      instanceId: card?.instanceId ?? card?.id,
    });
  }, [selectCard]);

  const handleSelectShopCard = useCallback((shopIdx: number, card: Card) => {
    sfx.click();
    selectCard({
      zone: 'shop',
      index: shopIdx,
      instanceId: card.instanceId ?? card.id,
    });
  }, [selectCard]);

  // Completes whichever slot-tap mode is active. Placement only lands on
  // empty slots (occupied slots keep their own tap = select); move lands
  // anywhere (occupied = swap, which reorderBoard already handles).
  const handleSlotTap = useCallback((slotIdx: number) => {
    const currentSelection = useUiSelectionStore.getState().selection;
    const isMoveArmed = useUiSelectionStore.getState().moveArmed;
    if (isMoveArmed && currentSelection?.zone === 'board') {
      if (currentSelection.index !== slotIdx) {
        sfx.click();
        reorderBoard?.(currentSelection.index, slotIdx);
      }
      clearSelection();
      return;
    }
    if (currentSelection?.zone === 'hand') {
      const currentBoard = useMatchStore.getState().board;
      if (currentBoard[slotIdx] === null) {
        sfx.success();
        playFromHand(currentSelection.index, slotIdx);
      }
      clearSelection();
    }
  }, [clearSelection, playFromHand, reorderBoard]);

  const handleSelectHeroPowerTarget = useCallback((index: number) => {
    selectHeroPowerTarget?.(index);
  }, [selectHeroPowerTarget]);

  // The selected card object, for the ActionBar's context line.
  const selectedCard: Card | null = useMemo(() => {
    if (!selection) return null;
    if (selection.zone === 'shop') return shop[selection.index] ?? null;
    if (selection.zone === 'hand') return hand[selection.index] ?? null;
    return board[selection.index] ?? null;
  }, [selection, shop, hand, board]);

  // Zone verbs for the ActionBar. Disabled actions stay visible with the
  // reason inline — "why can't I buy" used to be a transient toast.
  const barActions: ActionDescriptor[] = useMemo(() => {
    if (!selection || !selectedCard) return [];
    const info: ActionDescriptor = {
      id: 'info',
      label: 'Info',
      tone: 'neutral',
      onPress: () => {
        useCardDetailStore.getState().open(selectedCard);
      },
    };
    if (selection.zone === 'shop') {
      return [
        {
          id: 'buy',
          label: 'Buy',
          sublabel: '3g',
          tone: 'sodium',
          disabled: gold < 3 || handIsFull,
          disabledReason:
            gold < 3
              ? 'Not enough gold (need 3)'
              : handIsFull
                ? 'Hand full (max 10)'
                : undefined,
          onPress: () => {
            handleBuy(selectedCard.instanceId!);
            clearSelection();
          },
        },
        info,
      ];
    }
    if (selection.zone === 'hand') {
      return [
        {
          id: 'play',
          label: 'Play',
          tone: 'sodium',
          disabled: !boardHasRoom,
          disabledReason: !boardHasRoom ? 'Board full' : undefined,
          onPress: () => {
            sfx.success();
            playFromHand(selection.index);
            clearSelection();
          },
        },
        info,
      ];
    }
    // board
    return [
      {
        id: 'sell',
        label: 'Sell',
        sublabel: '+1g',
        tone: 'red',
        onPress: () => {
          handleSellFromBoard(selection.index);
          clearSelection();
        },
      },
      {
        id: 'move',
        label: 'Move',
        tone: 'ice',
        onPress: () => {
          sfx.click();
          armMove();
        },
      },
      info,
    ];
  }, [
    selection,
    selectedCard,
    gold,
    handIsFull,
    boardHasRoom,
    handleBuy,
    clearSelection,
    playFromHand,
    handleSellFromBoard,
    armMove,
  ]);

  // Scouting drawer — lobby roster panel toggled from the rail.
  const [scoutingOpen, setScoutingOpen] = useState(false);
  const [synergyOpen, setSynergyOpen] = useState(false);

  const handleOpenScouting = useCallback(() => {
    sfx.click();
    setScoutingOpen(true);
  }, []);

  const handleCloseScouting = useCallback(() => {
    setScoutingOpen(false);
  }, []);

  const handleOpenSynergy = useCallback(() => {
    sfx.click();
    setSynergyOpen(true);
  }, []);

  const handleCloseSynergy = useCallback(() => {
    setSynergyOpen(false);
  }, []);

  // Transient toast — used to surface "hand full" or "not enough gold"
  // failures with text instead of just sfx.error. Auto-clears after 2s.
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 2000);
    return () => window.clearTimeout(id);
  }, [toast]);

  if (!urlMatchId || storedMatchId !== urlMatchId || !heroId || picks.length === 0) {
    return <Navigate to="/play" replace />;
  }

  const opponentsAliveCount = opponents.filter((o) => o.alive).length;
  const boardUnitsCount = board.filter(Boolean).length;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt lg:min-h-0">
        {/* Swap-meet stall under string lights. This is the densest screen in the
            game — shop rail, board, hand rail, header chrome — so the plate is held
            back hardest of all (0.82). It is here for the warmth the string lights
            give the asphalt, not to be read as a location. */}
        <ScreenPlate src="plates/tavern_counter.webp" scrim={0.82} position="center 40%" />

        <div
          aria-hidden
          className="pointer-events-none absolute -left-1/4 -top-1/4 h-[40vw] w-[40vw] max-h-[520px] max-w-[520px]"
          style={{
            background:
              'radial-gradient(circle, rgba(255,138,61,0.12) 0%, rgba(255,138,61,0) 70%)',
          }}
        />

        {/* Bottom padding reserves the hand rail's band for the WHOLE
            screen — not just the card column. The action rail is a sibling
            of that column, so padding the column alone left End Turn
            underneath the hand. */}
        <section
          className="relative mx-auto flex h-full w-full max-w-7xl flex-col gap-[2px] overflow-hidden px-xs py-[2px] sm:gap-xs sm:px-sm sm:py-xs md:gap-md md:px-lg md:py-md lg:gap-md lg:px-xl lg:py-md"
          style={{
            paddingBottom: `calc(${handRailClearance(layoutMode)} + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          {/* Top bar — React.memo'd HUD zone */}
          <TavernHud
            health={health}
            gold={gold}
            roundNumber={roundNumber}
            tavernTier={tavernTier}
            nextTierCost={tavernTier >= MAX_TAVERN_TIER ? null : nextTierCost}
            rerollCost={rerollCost}
            healFx={healFx}
            goldFx={goldFx}
            sellGainPopup={sellGainPopup}
            onDismissSellGain={handleDismissSellGain}
            opponentsAliveCount={opponentsAliveCount}
            opponentsCount={opponents.length}
            boardPower={boardPower}
            boardUnitsCount={boardUnitsCount}
            turnTimerMs={turnTimerMs}
            turnTimerActive={turnTimerActive}
            tavernBlocked={tavernBlocked}
            hero={hero}
            layoutMode={layoutMode}
            portrait={portrait}
            reducedMotion={reducedMotion}
            tribeProgress={tribeProgress}
            onOpenSynergy={handleOpenSynergy}
          />

          {/* Main columns — side-by-side on tablet+, stacked on landscape
              mobile so the action bar slides under the main column instead
              of stealing horizontal real estate. */}
          <div className="flex flex-1 flex-row gap-[2px] overflow-hidden [@media(min-height:501px)]:gap-xs sm:gap-sm md:gap-md lg:gap-xl">
            {/* Main column — owns the available vertical space. */}
            <div
              className={`flex flex-1 flex-col gap-[1px] overflow-hidden sm:gap-[2px] md:gap-md lg:gap-sm ${
                layoutMode === 'phoneLandscape'
                  ? 'justify-center'
                  : portrait
                    ? 'justify-between'
                    : ''
              }`}
            >
              {/* SHOP — React.memo'd zone sub-component */}
              <ShopZone
                shop={shop}
                tavernTier={tavernTier}
                shopRailed={shopRailed}
                shopZoneStyle={shopZoneStyle}
                shopCardCap={shopCardCap}
                targetingShop={targetingShop}
                tripleCandidateIds={tripleCandidateIds}
                copiesById={copiesById}
                canBuy={canBuyShopCard}
                onConfirmBuy={handleConfirmBuy}
                shopFxByInstance={shopFxByInstance}
                selection={selection}
                heroPowerState={heroPowerState}
                reducedMotion={reducedMotion}
                cardSize={cardSize}
                layoutMode={layoutMode}
                handIsFull={handIsFull}
                portrait={portrait}
                shopRailRef={shopRailRef}
                shopProgress={shopProgress}
                setShopProgress={setShopProgress}
                onSelectCard={handleSelectShopCard}
                onSelectHeroPowerTarget={handleSelectHeroPowerTarget}
              />

              {/* BOARD — React.memo'd zone sub-component */}
              <BoardZone
                board={board}
                boardRailed={boardRailed}
                boardZoneStyle={boardZoneStyle}
                boardCardCap={boardCardCap}
                cardSize={cardSize}
                activeDrag={activeDrag}
                targetingUnits={targetingUnits}
                reducedMotion={reducedMotion}
                boardFxBySlot={boardFxBySlot}
                selection={selection}
                slotTapArmed={slotTapArmed}
                moveArmed={moveArmed}
                layoutMode={layoutMode}
                portrait={portrait}
                boardRailRef={boardRailRef}
                boardProgress={boardProgress}
                setBoardProgress={setBoardProgress}
                onSelectBoard={handleSelectBoard}
                onSlotTap={handleSlotTap}
                onSelectHeroPowerTarget={handleSelectHeroPowerTarget}
              />
            </div>

            {/* Action rail (landscape/desktop) — inside React.memo'd ActionZone */}
            {layoutMode !== 'phonePortrait' && (
              <ActionZone
                layoutMode={layoutMode}
                gold={gold}
                rerollCost={rerollCost}
                shopFrozen={shopFrozen}
                tavernTier={tavernTier}
                nextTierCost={nextTierCost}
                canUpgrade={canUpgrade}
                opponentsAliveCount={opponentsAliveCount}
                hero={hero}
                heroPowerLabel={heroPowerLabel}
                heroPowerState={heroPowerState}
                heroPowerAvailable={heroPowerAvailable}
                heroPowerIsActive={heroPowerIsActive}
                heroPowerCost={heroPowerCost}
                heroPowerCanAfford={heroPowerCanAfford}
                heroPowerUsedThisTurn={heroPowerUsedThisTurn}
                reducedMotion={reducedMotion}
                activeDrag={activeDrag}
                onReroll={handleReroll}
                onToggleFreeze={handleToggleFreeze}
                onUpgrade={handleUpgrade}
                onOpenScout={handleOpenScouting}
                onActivateHeroPower={handleActivateHeroPower}
                onCancelHeroPower={handleCancelHeroPower}
                onEndTurn={handleEndTurn}
              />
            )}
          </div>

          {/* Portrait action dock — inside React.memo'd ActionZone */}
          {layoutMode === 'phonePortrait' && (
            <ActionZone
              layoutMode={layoutMode}
              gold={gold}
              rerollCost={rerollCost}
              shopFrozen={shopFrozen}
              tavernTier={tavernTier}
              nextTierCost={nextTierCost}
              canUpgrade={canUpgrade}
              opponentsAliveCount={opponentsAliveCount}
              hero={hero}
              heroPowerLabel={heroPowerLabel}
              heroPowerState={heroPowerState}
              heroPowerAvailable={heroPowerAvailable}
              heroPowerIsActive={heroPowerIsActive}
              heroPowerCost={heroPowerCost}
              heroPowerCanAfford={heroPowerCanAfford}
              heroPowerUsedThisTurn={heroPowerUsedThisTurn}
              reducedMotion={reducedMotion}
              activeDrag={activeDrag}
              onReroll={handleReroll}
              onToggleFreeze={handleToggleFreeze}
              onUpgrade={handleUpgrade}
              onOpenScout={handleOpenScouting}
              onActivateHeroPower={handleActivateHeroPower}
              onCancelHeroPower={handleCancelHeroPower}
              onEndTurn={handleEndTurn}
            />
          )}
        </section>

        {/* Hand — portaled overlay rail pinned to the bottom edge. Always
            above the tavern layout, but slides out during any drag so it
            never blocks the board's lower row or the sell strip. */}
        <HandRail
          hand={hand}
          canPlay={boardHasRoom}
          // Tap = select: same grammar as shop and board. Play lives in the
          // ActionBar (or drag, or an empty-slot tap while selected).
          onTapSelect={(i) => {
            sfx.click();
            selectCard({
              zone: 'hand',
              index: i,
              instanceId: hand[i]?.instanceId ?? hand[i]?.id,
            });
          }}
          selectedIndex={selection?.zone === 'hand' ? selection.index : null}
          // Landscape hand cards are ~72px wide — too narrow for a card foot
          // (the rules text overflowed the rail), so icon mode is the honest
          // fit there and costs nothing now that tap opens the full card.
          // Portrait's taller band makes them wide enough to carry the foot.
          size={layoutMode === 'phoneLandscape' ? 'sm' : 'md'}
          dragging={activeDrag !== null}
          suppressed={
            scoutingOpen ||
            synergyOpen ||
            heroPowerState === 'discovering' ||
            tavernBlocked
          }
          reducedMotion={reducedMotion}
        />

        {/* Action bar — the tap-select grammar's verb surface. Anchored above
            the hand band so switching selection stays one tap away. */}
        <ActionBar
          open={selection !== null && !tavernBlocked && activeDrag === null}
          card={selectedCard}
          hint={
            moveArmed
              ? 'tap a slot to move it there'
              : selection?.zone === 'hand' && boardHasRoom
                ? 'play, or tap an empty slot to place'
                : undefined
          }
          actions={barActions}
          onDismiss={clearSelection}
          reducedMotion={reducedMotion}
        />

        {/* Sell strip — fixed footer band, visible while dragging a board card */}
        <SellStrip visible={sellStripVisible} />

        <ScoutingPanel
          open={scoutingOpen}
          onClose={handleCloseScouting}
          opponents={opponents}
          roundNumber={roundNumber}
        />

        {/* Every tribe, always — the reference view the game never had.
            Opened from the header chip row. */}
        <SynergyPanel
          open={synergyOpen}
          onClose={handleCloseSynergy}
          board={board}
          hand={hand}
          shop={shop}
        />

        {/* Transient toast — surfaces blocked-action reasons like "hand full"
            so the sfx.error isn't silent. Auto-clears after 2s. */}
        <AnimatePresence>
          {toast && (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="pointer-events-none fixed inset-x-0 top-md z-40 flex justify-center md:top-lg"
              role="status"
              aria-live="polite"
            >
              <span
                className="rounded-sm border bg-stucco2 px-md py-xs font-data text-[12px] uppercase tracking-[0.2em] text-aerosolRed shadow-sodium md:text-[13px]"
                style={{ borderColor: 'rgba(230,52,42,0.4)' }}
              >
                {toast.message}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Triple-reward discover — legacy R6 path. Gated behind the
          flourish so the modal opens AFTER the celebratory overlay clears. */}
      <DiscoverModal
        pendingTripleReward={tripleFlourishing ? null : pendingTripleReward}
        discoverOptions={discoverOptions}
        onClaim={(card) => claimDiscoverReward?.(card)}
      />

      {/* Triple-formed flourish — covers the tavern with a sodium burst +
          big "TRIPLE!" text for ~700ms before the discover modal opens.
          Reduced motion skips this entirely (see effect above). */}
      <AnimatePresence>
        {tripleFlourishing && (
          <motion.div
            key="triple-flourish"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center"
            role="presentation"
          >
            <div
              aria-hidden
              className="absolute inset-0 bg-asphalt/55"
            />
            {/* Expanding sodium ring */}
            <motion.div
              aria-hidden
              initial={{ scale: 0, opacity: 0.9 }}
              animate={{ scale: 3.5, opacity: 0 }}
              transition={{ duration: 0.65, ease: 'easeOut' }}
              className="absolute h-32 w-32 rounded-full border-4 border-sodium"
              style={{ boxShadow: '0 0 60px 20px rgba(255,138,61,0.55)' }}
            />
            {/* TRIPLE! text */}
            <motion.h2
              initial={{ scale: 0.4, opacity: 0, rotate: -6 }}
              animate={{
                scale: [0.4, 1.15, 1],
                opacity: [0, 1, 1],
                rotate: [-6, 2, 0],
              }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              className="relative font-cholo text-[56px] leading-none text-sodium md:text-[88px] lg:text-[120px]"
              style={{ textShadow: '0 0 24px rgba(255,138,61,0.7)' }}
            >
              TRIPLE!
            </motion.h2>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero-power discover — generalized S3 path. Uses the same
          visual chrome as the triple modal, with hero-themed copy. */}
      <DiscoverModal
        open={heroPowerState === 'discovering'}
        options={heroPowerDiscoverOptions}
        onSelect={(card) => claimHeroPowerDiscover?.(card)}
        headerCopy={{
          eyebrow: 'hero power',
          title: hero ? `${hero.name} · Discover` : 'Hero Power · Discover',
          hint: 'pick one · arrows + enter, or tap',
        }}
      />

      {/* Battlecry Discover — generalized path, same chrome. Queued behind
          the triple reward and hero power via `abilityDiscoverOpen`, so at
          most one discover modal is ever on screen. */}
      <DiscoverModal
        open={abilityDiscoverOpen}
        options={abilityDiscoverOptions}
        onSelect={(card) => claimAbilityDiscover?.(card)}
        headerCopy={{
          eyebrow: 'summon',
          title: pendingAbilityDiscover
            ? `${pendingAbilityDiscover.sourceName} · Discover`
            : 'Discover',
          hint: 'pick one · arrows + enter, or tap',
        }}
      />

      {/* Hero-power targeting overlay — sodium pulse on board/shop is
          handled inline above; this component owns the dim backdrop,
          banner, and ESC/cancel wiring. */}
      <HeroPowerTargetingOverlay
        open={heroPowerState === 'targeting'}
        targetType={heroPowerTargetType}
        onCancel={() => cancelHeroPower?.()}
        powerName={hero?.name}
      />

      {/* Drag overlay — gives the lifted card its scale/tilt/glow */}
      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.32, 1.34)' }}>
        {draggedCard ? (
          <div
            className="pointer-events-none w-[72px] sm:w-[84px] md:w-[104px] lg:w-[116px]"
            style={{
              transform: 'scale(1.06) rotate(-3deg)',
              filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.55)) drop-shadow(0 0 12px rgba(255,138,61,0.25))',
            }}
          >
            <CardTile card={draggedCard} size={cardSize} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// --- Zone Sub-components ---

interface TavernHudProps {
  health: number;
  gold: number;
  roundNumber: number;
  tavernTier: number;
  nextTierCost: number | null;
  rerollCost: number;
  healFx: Extract<TavernFxEvent, { kind: 'hero_heal' }>[];
  goldFx: Extract<TavernFxEvent, { kind: 'gold_gain' }>[];
  sellGainPopup: { id: number; amount: number } | null;
  onDismissSellGain: () => void;
  opponentsAliveCount: number;
  opponentsCount: number;
  boardPower: { atk: number; hp: number };
  boardUnitsCount: number;
  turnTimerMs: number;
  turnTimerActive: boolean;
  tavernBlocked: boolean;
  hero: Hero | undefined;
  layoutMode: string;
  portrait: boolean;
  reducedMotion: boolean;
  tribeProgress: ReturnType<typeof allTribeProgress>;
  onOpenSynergy: () => void;
}

const TavernHud = React.memo(function TavernHud({
  health,
  gold,
  roundNumber,
  tavernTier,
  nextTierCost,
  rerollCost,
  healFx,
  goldFx,
  sellGainPopup,
  onDismissSellGain,
  opponentsAliveCount,
  opponentsCount,
  boardPower,
  boardUnitsCount,
  turnTimerMs,
  turnTimerActive,
  tavernBlocked,
  hero,
  layoutMode,
  portrait,
  reducedMotion,
  tribeProgress,
  onOpenSynergy,
}: TavernHudProps) {
  return (
    <header className="flex flex-col gap-[2px] md:gap-sm">
      <div
        className={`flex items-center justify-between rounded-md border bg-stucco1 px-xs py-[2px] sm:px-sm sm:py-[4px] ${
          layoutMode === 'desktop'
            ? 'flex-wrap gap-xs md:gap-md md:px-lg md:py-md'
            : 'flex-nowrap gap-[3px]'
        }`}
        style={{ borderColor: EDGES.soft }}
      >
        <span className="relative inline-flex">
          <Stat
            label="HP"
            value={health}
            accent="aerosolRed"
            explain="tavern.hp"
          />
          <AnimatePresence>
            {healFx.map((e) => (
              <ChipFloater key={e.id} text={`+${e.amount}`} toneClass="text-glowStick" />
            ))}
          </AnimatePresence>
        </span>
        <span className="relative inline-flex">
          <Stat
            label="Gold"
            value={gold}
            accent="sodium"
            explain="tavern.gold"
            detail={`Cards cost 3g · reroll ${rerollCost === 0 ? 'free' : `${rerollCost}g`}`}
          />
          <AnimatePresence>
            {sellGainPopup && (
              <motion.span
                key={sellGainPopup.id}
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: -22 }}
                exit={{ opacity: 0, y: -32 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
                onAnimationComplete={onDismissSellGain}
                className="pointer-events-none absolute -top-[2px] left-0 font-data text-[12px] font-bold text-sodium md:text-[14px]"
                aria-hidden
              >
                +{sellGainPopup.amount}g
              </motion.span>
            )}
            {goldFx.map((e) => (
              <ChipFloater key={e.id} text={`+${e.amount}g`} toneClass="text-sodium" />
            ))}
          </AnimatePresence>
        </span>
        <Stat label="Round" value={roundNumber} accent="bone" explain="tavern.round" />
        <TierStat
          value={tavernTier}
          reducedMotion={reducedMotion}
          nextCost={nextTierCost}
        />
        <span className={layoutMode === 'desktop' ? 'contents' : 'hidden'}>
          <PlacementChip
            aliveCount={
              (health > 0 ? 1 : 0) + opponentsAliveCount
            }
            totalCount={1 + opponentsCount}
          />
          <BoardPowerChip
            atk={boardPower.atk}
            hp={boardPower.hp}
            units={boardUnitsCount}
          />
        </span>
        <Explain
          id="tavern.timer"
          side="bottom"
          detail={
            turnTimerActive && !tavernBlocked
              ? undefined
              : 'Paused — finish the discover or hero power to resume'
          }
          className="inline-flex"
        >
          <TurnTimer
            remainingMs={turnTimerMs}
            totalMs={TURN_DURATION_MS}
            active={turnTimerActive && !tavernBlocked}
            size={portrait ? 44 : undefined}
          />
        </Explain>
        {hero && layoutMode === 'desktop' && (
          <span
            className={`font-data text-[11px] uppercase tracking-[0.2em] md:text-[13px] lg:text-[14px] ${
              hero.faction === 'cholo' ? 'text-cholo' : 'text-emo'
            }`}
          >
            {hero.name}
          </span>
        )}
        {layoutMode === 'desktop' ? (
          <Link
            to="/menu"
            className="ml-auto shrink-0 font-data text-[10px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px]"
          >
            ← menu
          </Link>
        ) : (
          <Link
            to="/menu"
            aria-label="Back to menu"
            className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-sm border border-stucco3 bg-stucco2 font-data text-[16px] text-graphite hover:text-marker"
          >
            ←
          </Link>
        )}
      </div>
      <SynergyChipRow
        progress={tribeProgress}
        compact
        limit={
          layoutMode === 'desktop'
            ? undefined
            : layoutMode === 'phonePortrait'
              ? 3
              : 2
        }
        onOpen={onOpenSynergy}
      />
    </header>
  );
});

interface ShopZoneProps {
  shop: Card[];
  tavernTier: number;
  shopRailed: boolean;
  shopZoneStyle: React.CSSProperties;
  shopCardCap: React.CSSProperties | undefined;
  targetingShop: boolean;
  tripleCandidateIds: Set<string>;
  /** Copies already held (hand + board), keyed by card id — triple progress. */
  copiesById: Map<string, number>;
  /** Whether a shop card can be bought right now. Mirrors the Buy verb's own
      enable rule exactly, so the tile can never promise what the bar refuses. */
  canBuy: boolean;
  /** Second tap on an already-selected shop card buys it. */
  onConfirmBuy: (card: Card) => void;
  shopFxByInstance: Map<string, Extract<TavernFxEvent, { kind: 'shop_buff' }>[]>;
  selection: { zone: string; index: number; instanceId?: string } | null;
  heroPowerState: 'idle' | 'targeting' | 'discovering' | 'used';
  reducedMotion: boolean;
  cardSize: CardSize;
  layoutMode: string;
  handIsFull: boolean;
  portrait: boolean;
  shopRailRef: React.RefObject<RailHandle | null>;
  shopProgress: RailProgress | null;
  setShopProgress: (p: RailProgress) => void;
  onSelectCard: (shopIdx: number, card: Card) => void;
  onSelectHeroPowerTarget?: (index: number) => void;
}

const ShopZone = React.memo(function ShopZone({
  shop,
  tavernTier,
  shopRailed,
  shopZoneStyle,
  shopCardCap,
  targetingShop,
  tripleCandidateIds,
  copiesById,
  canBuy,
  onConfirmBuy,
  shopFxByInstance,
  selection,
  heroPowerState,
  reducedMotion,
  cardSize,
  layoutMode,
  handIsFull,
  portrait,
  shopRailRef,
  shopProgress,
  setShopProgress,
  onSelectCard,
  onSelectHeroPowerTarget,
}: ShopZoneProps) {
  const tripleInShop = useMemo(
    () => shop.some((c) => c && tripleCandidateIds.has(c.id)),
    [shop, tripleCandidateIds],
  );

  return (
    <Section
      label={`Shop · Tier ${tavernTier}+`}
      lift={targetingShop}
      roomy={layoutMode === 'desktop'}
      grow={portrait}
      growWeight={1.3}
    >
      <div
        className={`rounded-md border p-xs sm:p-sm ${
          shopRailed ? 'flex min-h-0 flex-col justify-center' : ''
        }`}
        style={{
          ...(shopRailed ? shopZoneStyle : null),
          borderColor: 'rgba(255,138,61,0.28)',
          background:
            'linear-gradient(160deg, rgba(255,138,61,0.16) 0%, rgba(255,138,61,0.05) 45%, rgba(26,22,18,0) 100%)',
        }}
      >
        <RailOrGrid
          railed={shopRailed}
          label={`Shop, ${shop.length} cards`}
          fadeColor="rgba(38,26,18,0.96)"
          gridClass="grid min-h-0 grid-cols-5 gap-xs md:gap-md lg:gap-md"
          railClass="min-h-0 items-center gap-xs pb-[2px]"
          railRef={shopRailRef}
          onProgressChange={setShopProgress}
          snap
        >
          {shop.length === 0 ? (
            <p className="col-span-full text-center font-data text-[11px] uppercase tracking-[0.2em] text-graphite md:text-[13px]">
              shop empty — reroll
            </p>
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
              {shop.map((card, shopIdx) => {
                const isShopTarget = targetingShop;
                const isTripleCandidate =
                  !isShopTarget && tripleCandidateIds.has(card.id);
                const cardFx = card.instanceId
                  ? shopFxByInstance.get(card.instanceId) ?? EMPTY_CARD_FX
                  : EMPTY_CARD_FX;
                return (
                  <motion.div
                    key={card.instanceId}
                    layout
                    initial={
                      reducedMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.8, y: -8 }
                    }
                    animate={
                      reducedMotion
                        ? { opacity: 1 }
                        : { opacity: 1, scale: 1, y: 0 }
                    }
                    exit={
                      reducedMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.7, y: 6 }
                    }
                    transition={{ duration: reducedMotion ? 0 : 0.18, ease: 'easeOut' }}
                    style={
                      shopRailed
                        ? { ...railItemStyle(), ...shopCardCap }
                        : undefined
                    }
                    data-selkeep
                    className={`relative flex min-h-0 items-center justify-center ${
                      shopRailed ? `h-full shrink-0 ${cardAspectClass(cardSize)}` : ''
                    } ${
                      isShopTarget
                        ? 'rounded-md ring-2 ring-sodium shadow-sodium animate-pulse'
                        : cardFx.length > 0
                          ? 'rounded-md ring-2 ring-sodium shadow-sodium'
                          : isTripleCandidate
                            ? 'rounded-md ring-2 ring-sodium/70 shadow-sodium'
                            : ''
                    }`}
                    title={isTripleCandidate ? 'Third copy — buy for a triple' : undefined}
                  >
                    <CardTile
                      card={card}
                      size={cardSize}
                      fill={shopRailed}
                      selected={
                        selection?.zone === 'shop' &&
                        selection.index === shopIdx
                      }
                      onClick={() => {
                        if (isShopTarget) {
                          onSelectHeroPowerTarget?.(shopIdx);
                          return;
                        }
                        if (heroPowerState === 'targeting') return;
                        onSelectCard(shopIdx, card);
                      }}
                      affordable={canBuy}
                      copiesOwned={copiesById.get(card.id) ?? 0}
                      // Only while the hero power is not mid-targeting: there a
                      // tap means "pick this target", and a second tap must not
                      // silently spend gold instead.
                      onConfirmTap={
                        isShopTarget || heroPowerState === 'targeting'
                          ? undefined
                          : () => onConfirmBuy(card)
                      }
                      disabled={
                        isShopTarget ? false : heroPowerState === 'targeting'
                      }
                    />
                    <AnimatePresence>
                      {cardFx.map((e) => (
                        <BuffFloater key={e.id} atk={e.atk} hp={e.hp} temp={false} />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </RailOrGrid>
        {shopRailed && (
          <RailDots progress={shopProgress} className="mt-[2px]" />
        )}
      </div>
      <p
        className={`mt-xs font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px] ${
          layoutMode === 'desktop' ? 'block' : 'hidden'
        }`}
      >
        {tripleInShop ? (
          <>
            <span className="text-sodium">★ glowing card → buy for a golden triple</span>
            {handIsFull ? ' · hand full' : ''}
          </>
        ) : (
          <>tap card · 3g to buy{handIsFull ? ' · hand full' : ''}</>
        )}
      </p>
    </Section>
  );
});

interface BoardZoneProps {
  board: (Card | null)[];
  boardRailed: boolean;
  boardZoneStyle: React.CSSProperties;
  boardCardCap: React.CSSProperties | undefined;
  cardSize: CardSize;
  activeDrag: DragSource | null;
  targetingUnits: boolean;
  reducedMotion: boolean;
  boardFxBySlot: Map<number, Extract<TavernFxEvent, { kind: 'board_buff' }>[]>;
  selection: { zone: string; index: number; instanceId?: string } | null;
  slotTapArmed: boolean;
  moveArmed: boolean;
  layoutMode: string;
  portrait: boolean;
  boardRailRef: React.RefObject<RailHandle | null>;
  boardProgress: RailProgress | null;
  setBoardProgress: (p: RailProgress) => void;
  onSelectBoard: (index: number) => void;
  onSlotTap: (index: number) => void;
  onSelectHeroPowerTarget?: (index: number) => void;
}

const BoardZone = React.memo(function BoardZone({
  board,
  boardRailed,
  boardZoneStyle,
  boardCardCap,
  cardSize,
  activeDrag,
  targetingUnits,
  reducedMotion,
  boardFxBySlot,
  selection,
  slotTapArmed,
  moveArmed,
  layoutMode,
  portrait,
  boardRailRef,
  boardProgress,
  setBoardProgress,
  onSelectBoard,
  onSlotTap,
  onSelectHeroPowerTarget,
}: BoardZoneProps) {
  const occupiedCount = useMemo(() => board.filter(Boolean).length, [board]);

  return (
    <Section
      label={`Board · ${occupiedCount}/${BOARD_SLOTS}`}
      lift={targetingUnits}
      roomy={layoutMode === 'desktop'}
      grow={portrait}
    >
      <div
        className={`rounded-md border p-xs sm:p-sm ${
          boardRailed ? 'flex min-h-0 flex-col justify-center' : ''
        }`}
        style={{
          ...(boardRailed ? boardZoneStyle : null),
          borderColor: 'rgba(122,160,190,0.22)',
          background:
            'linear-gradient(200deg, rgba(122,160,190,0.12) 0%, rgba(122,160,190,0.04) 50%, rgba(26,22,18,0) 100%)',
        }}
      >
        {boardRailed ? (
          <Rail
            ref={boardRailRef}
            label={`Board, ${occupiedCount} of ${BOARD_SLOTS} slots filled`}
            className="min-h-0 items-center gap-xs pb-[2px]"
            fadeColor="rgba(23,26,29,0.96)"
            onProgressChange={setBoardProgress}
            snap
          >
            {board.map((slot, i) => (
              <BoardSlotCell
                key={`slot-${i}`}
                index={i}
                card={slot}
                size={cardSize}
                railed
                activeDrag={activeDrag}
                targetingUnits={targetingUnits}
                onTargetSelect={onSelectHeroPowerTarget}
                reducedMotion={reducedMotion}
                fx={boardFxBySlot.get(i) ?? EMPTY_BOARD_FX_SLOT}
                selected={selection?.zone === 'board' && selection.index === i}
                onSelect={onSelectBoard}
                slotTapArmed={slotTapArmed}
                onSlotTap={onSlotTap}
                moveArmed={moveArmed}
                cardCap={boardCardCap}
              />
            ))}
          </Rail>
        ) : (
          <div className="grid min-h-0 grid-cols-7 gap-xs md:gap-md">
            {board.map((slot, i) => (
              <BoardSlotCell
                key={`slot-${i}`}
                index={i}
                card={slot}
                size={cardSize}
                activeDrag={activeDrag}
                targetingUnits={targetingUnits}
                onTargetSelect={onSelectHeroPowerTarget}
                reducedMotion={reducedMotion}
                fx={boardFxBySlot.get(i) ?? EMPTY_BOARD_FX_SLOT}
                selected={selection?.zone === 'board' && selection.index === i}
                onSelect={onSelectBoard}
                slotTapArmed={slotTapArmed}
                onSlotTap={onSlotTap}
                moveArmed={moveArmed}
              />
            ))}
          </div>
        )}
        {boardRailed && (
          <BoardMinimap
            board={board}
            progress={boardProgress}
            onChipTap={(i) => boardRailRef.current?.scrollToIndex(i, !reducedMotion)}
            className="mt-[2px]"
          />
        )}
      </div>
      <p
        className={`mt-xs font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px] ${
          layoutMode === 'desktop' ? 'block' : 'hidden'
        }`}
      >
        drag to reorder · drag to sell strip for +1g
      </p>
    </Section>
  );
});

interface ActionZoneProps {
  layoutMode: string;
  gold: number;
  rerollCost: number;
  shopFrozen: boolean;
  tavernTier: number;
  nextTierCost: number;
  canUpgrade: boolean;
  opponentsAliveCount: number;
  hero: Hero | undefined;
  heroPowerLabel: string;
  heroPowerState: 'idle' | 'targeting' | 'discovering' | 'used';
  heroPowerAvailable: boolean;
  heroPowerIsActive: boolean;
  heroPowerCost: number;
  heroPowerCanAfford: boolean;
  heroPowerUsedThisTurn: boolean;
  reducedMotion: boolean;
  activeDrag: DragSource | null;
  onReroll: () => void;
  onToggleFreeze: () => void;
  onUpgrade: () => void;
  onOpenScout: () => void;
  onActivateHeroPower: () => void;
  onCancelHeroPower: () => void;
  onEndTurn: () => void;
}

const ActionZone = React.memo(function ActionZone({
  layoutMode,
  gold,
  rerollCost,
  shopFrozen,
  tavernTier,
  nextTierCost,
  canUpgrade,
  opponentsAliveCount,
  hero,
  heroPowerLabel,
  heroPowerState,
  heroPowerAvailable,
  heroPowerIsActive,
  heroPowerCost,
  heroPowerCanAfford,
  heroPowerUsedThisTurn,
  reducedMotion,
  activeDrag,
  onReroll,
  onToggleFreeze,
  onUpgrade,
  onOpenScout,
  onActivateHeroPower,
  onCancelHeroPower,
  onEndTurn,
}: ActionZoneProps) {
  const renderActionButtons = (variant: 'column' | 'dock') => (
    <>
      <RailButton
        onClick={onReroll}
        disabled={gold < rerollCost}
        label="Reroll"
        icon="⟳"
        variant={variant}
        cost={rerollCost === 0 ? 'free' : `${rerollCost}g`}
        tone="sodium"
        explain="tavern.reroll"
        detail={
          rerollCost === 0
            ? 'Your hero makes the first reroll each turn free'
            : gold < rerollCost
              ? 'Not enough gold'
              : undefined
        }
      />
      <RailButton
        onClick={onToggleFreeze}
        label={shopFrozen ? 'Unfreeze' : 'Freeze'}
        icon="❄"
        variant={variant}
        cost={shopFrozen ? 'on' : 'off'}
        tone="ice"
        active={shopFrozen}
        explain="tavern.freeze"
        detail={shopFrozen ? 'On — this lineup carries to next turn' : undefined}
      />
      <RailButton
        onClick={onUpgrade}
        disabled={!canUpgrade}
        label={`Upgrade T${tavernTier + 1}`}
        icon="▲"
        variant={variant}
        cost={tavernTier >= MAX_TAVERN_TIER ? 'max' : `${nextTierCost}g`}
        tone="tecate"
        explain="tavern.upgrade"
        detail={
          tavernTier >= MAX_TAVERN_TIER
            ? 'Already at max tier'
            : gold < nextTierCost
              ? `Needs ${nextTierCost}g — you have ${gold}g`
              : `Unlocks tier ${tavernTier + 1} cards`
        }
      />
      <RailButton
        onClick={onOpenScout}
        label="Scout"
        icon="◎"
        variant={variant}
        cost={`${opponentsAliveCount}↑`}
        tone="graphite"
        explain="tavern.scout"
        detail="Free — costs no gold and no time"
      />
      <HeroPowerRailButton
        label={heroPowerLabel}
        variant={variant}
        description={hero?.powerDescription}
        state={heroPowerState}
        available={heroPowerAvailable}
        isActive={heroPowerIsActive}
        cost={heroPowerCost}
        canAfford={heroPowerCanAfford}
        usedThisTurn={heroPowerUsedThisTurn}
        onActivate={onActivateHeroPower}
        onCancel={onCancelHeroPower}
        reducedMotion={reducedMotion}
      />
    </>
  );

  if (layoutMode !== 'phonePortrait') {
    return (
      <aside
        className="flex w-[112px] shrink-0 flex-col gap-[3px] rounded-md border bg-stucco1 p-[3px] sm:w-[124px] sm:gap-xs sm:p-sm md:w-[200px] md:gap-md md:p-lg lg:w-[280px]"
        style={{ borderColor: EDGES.soft }}
      >
        {renderActionButtons('column')}
        <div className="mt-auto h-px w-auto bg-stucco3" />
        <Explain
          id="tavern.endTurn"
          side="left"
          touch="longpress"
          detail={gold > 0 ? `${gold}g still unspent` : undefined}
          className="flex shrink-0"
        >
          <motion.button
            onClick={onEndTurn}
            whileTap={reducedMotion ? undefined : { scale: 0.97 }}
            aria-label="End turn"
            className="min-h-[44px] w-full shrink-0 rounded-sm bg-aerosolRed px-xs font-data text-[11px] uppercase tracking-[0.15em] text-bone hover:bg-hotPink sm:text-[12px] sm:tracking-[0.2em] md:min-h-[56px] md:text-[15px] lg:min-h-[64px] lg:text-[17px]"
          >
            End Turn ›
          </motion.button>
        </Explain>
      </aside>
    );
  }

  return (
    <div
      className={`flex shrink-0 items-stretch gap-[6px] rounded-md border bg-stucco1 p-[4px] transition-opacity ${
        activeDrag ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
      style={{ borderColor: EDGES.soft }}
    >
      {renderActionButtons('dock')}
      <Explain
        id="tavern.endTurn"
        side="top"
        touch="longpress"
        detail={gold > 0 ? `${gold}g still unspent` : undefined}
        className="flex min-w-0 flex-1"
      >
        <motion.button
          onClick={onEndTurn}
          whileTap={reducedMotion ? undefined : { scale: 0.97 }}
          aria-label="End turn"
          className="min-h-[48px] w-full rounded-sm bg-aerosolRed px-sm font-data text-[13px] uppercase tracking-[0.15em] text-bone hover:bg-hotPink"
        >
          End Turn ›
        </motion.button>
      </Explain>
    </div>
  );
});

// --- Leaf Sub-components ---

const RailOrGrid = React.memo(function RailOrGrid({
  railed,
  label,
  fadeColor,
  gridClass,
  railClass,
  children,
  railRef,
  onProgressChange,
  snap = false,
}: {
  railed: boolean;
  label: string;
  fadeColor: string;
  gridClass: string;
  railClass: string;
  children: React.ReactNode;
  railRef?: React.Ref<RailHandle>;
  onProgressChange?: (p: RailProgress) => void;
  snap?: boolean;
}) {
  if (!railed) return <div className={gridClass}>{children}</div>;
  return (
    <Rail
      ref={railRef}
      label={label}
      fadeColor={fadeColor}
      className={railClass}
      onProgressChange={onProgressChange}
      snap={snap}
    >
      {children}
    </Rail>
  );
});

const Section = React.memo(function Section({
  label,
  children,
  lift,
  grow,
  growWeight = 1,
  roomy,
}: {
  label: string;
  children: React.ReactNode;
  roomy?: boolean;
  lift?: boolean;
  grow?: boolean;
  growWeight?: number;
}) {
  return (
    <div
      className={`flex min-h-0 flex-col ${grow ? 'flex-1' : ''} ${
        lift ? 'relative z-40' : ''
      }`}
      style={grow ? { flexGrow: growWeight } : undefined}
    >
      <h2
        className={`font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[13px] lg:text-[14px] ${
          roomy ? 'block' : 'hidden'
        }`}
      >
        {label}
      </h2>
      <div
        className={`${roomy ? 'md:mt-sm' : ''} ${
          grow ? 'flex min-h-0 flex-1 flex-col' : ''
        }`}
      >
        {children}
      </div>
    </div>
  );
});

const accentToBg: Record<string, string> = {
  aerosolRed: 'bg-aerosolRed',
  sodium: 'bg-sodium',
  bone: 'bg-stucco3',
  tecate: 'bg-tecate',
};
const accentToText: Record<string, string> = {
  aerosolRed: 'text-bone',
  sodium: 'text-sharpie',
  bone: 'text-bone',
  tecate: 'text-sharpie',
};

const Stat = React.memo(function Stat({
  label,
  value,
  accent,
  explain,
  detail,
}: {
  label: string;
  value: number;
  accent: string;
  explain: ExplainId;
  detail?: string;
}) {
  return (
    <Explain
      id={explain}
      detail={detail}
      side="bottom"
      className="inline-flex items-center gap-[3px] sm:gap-xs"
    >
      <span
        className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-sm px-[3px] font-data text-[11px] font-bold sm:h-6 sm:min-w-[26px] sm:px-xs sm:text-[13px] md:h-9 md:min-w-[36px] md:text-[16px] lg:h-10 lg:min-w-[44px] lg:text-[18px] ${accentToBg[accent]} ${accentToText[accent]}`}
      >
        {value}
      </span>
      <span className="font-data text-[10px] uppercase tracking-[0.15em] text-graphite [@media(max-height:500px)]:hidden sm:tracking-[0.3em] md:text-[11px] lg:text-[12px]">
        {label}
      </span>
    </Explain>
  );
});

const TierStat = React.memo(function TierStat({
  value,
  reducedMotion,
  nextCost,
}: {
  value: number;
  reducedMotion: boolean;
  nextCost: number | null;
}) {
  const controls = useAnimationControls();
  const prevRef = useRef(value);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = value;
    if (reducedMotion) return;
    if (value > prev) {
      controls.start({
        scale: [1, 1.25, 1],
        boxShadow: [
          '0 0 0px 0px rgba(212,169,69,0)',
          '0 0 20px 6px rgba(212,169,69,0.7)',
          '0 0 0px 0px rgba(212,169,69,0)',
        ],
        transition: { duration: 0.6, ease: 'easeOut' },
      });
    }
  }, [value, controls, reducedMotion]);
  return (
    <Explain
      id="tavern.tier"
      side="bottom"
      detail={
        nextCost === null
          ? `Tier ${value} · max`
          : `Tier ${value} · next costs ${nextCost}g`
      }
      className="inline-flex items-center gap-xs"
    >
      <motion.span
        animate={controls}
        className="inline-flex h-7 min-w-[28px] items-center justify-center rounded-sm bg-tecate px-xs font-data text-[14px] font-bold text-sharpie md:h-9 md:min-w-[36px] md:text-[16px] lg:h-10 lg:min-w-[44px] lg:text-[18px]"
      >
        {value}
      </motion.span>
      <span className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite md:text-[11px] lg:text-[12px]">
        Tier
      </span>
    </Explain>
  );
});

const PlacementChip = React.memo(function PlacementChip({
  aliveCount,
  totalCount,
}: {
  aliveCount: number;
  totalCount: number;
}) {
  const last = aliveCount <= 2;
  return (
    <Explain
      id="tavern.placement"
      side="bottom"
      detail={last ? 'Top 2 — next loss could end it' : `Finishing ${aliveCount}th or better`}
      className="inline-flex items-center gap-xs"
    >
      <span
        aria-label={`${aliveCount} of ${totalCount} players left`}
        className={`inline-flex h-7 min-w-[36px] items-center justify-center rounded-sm px-xs font-data text-[12px] font-bold md:h-9 md:min-w-[44px] md:text-[14px] lg:h-10 lg:min-w-[52px] lg:text-[15px] ${
          last ? 'bg-aerosolRed text-bone' : 'bg-stucco2 text-bone'
        }`}
      >
        {aliveCount}/{totalCount}
      </span>
      <span className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite md:text-[11px] lg:text-[12px]">
        Left
      </span>
    </Explain>
  );
});

const BoardPowerChip = React.memo(function BoardPowerChip({
  atk,
  hp,
  units,
}: {
  atk: number;
  hp: number;
  units: number;
}) {
  return (
    <Explain
      id="tavern.boardPower"
      side="bottom"
      detail={`${units} of ${BOARD_SLOTS} slots filled`}
      className="inline-flex items-center gap-xs"
    >
      <span
        aria-label={`board power ${atk} attack, ${hp} health`}
        className="inline-flex h-7 items-center gap-[3px] rounded-sm bg-stucco2 px-sm font-data text-[12px] font-bold md:h-9 md:text-[14px] lg:h-10 lg:text-[15px]"
      >
        <span className="text-aerosolRed">{atk}</span>
        <span className="text-graphite">/</span>
        <span className="text-glowStick">{hp}</span>
      </span>
      <span className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite md:text-[11px] lg:text-[12px]">
        Board
      </span>
    </Explain>
  );
});

const BoardSlotCell = React.memo(function BoardSlotCell({
  index,
  card,
  size,
  railed = false,
  activeDrag,
  targetingUnits,
  onTargetSelect,
  reducedMotion,
  fx = EMPTY_BOARD_FX_SLOT,
  selected = false,
  onSelect,
  slotTapArmed = false,
  onSlotTap,
  moveArmed = false,
  cardCap,
}: {
  index: number;
  card: Card | null;
  size: CardSize;
  railed?: boolean;
  activeDrag: DragSource | null;
  targetingUnits: boolean;
  onTargetSelect?: (index: number) => void;
  reducedMotion: boolean;
  fx?: { id: number; atk: number; hp: number; temp: boolean }[];
  selected?: boolean;
  onSelect?: (index: number) => void;
  slotTapArmed?: boolean;
  onSlotTap?: (index: number) => void;
  moveArmed?: boolean;
  cardCap?: React.CSSProperties;
}) {
  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: `slot:${index}`,
    data: { kind: 'board-slot', index },
  });

  const isValidDropTarget =
    isOver &&
    activeDrag !== null &&
    !(activeDrag.kind === 'board' && activeDrag.index === index);

  const railBox = railed ? `h-full shrink-0 ${cardAspectClass(size)}` : 'w-full';

  if (!card) {
    return (
      <div
        ref={setDropRef}
        data-selkeep={slotTapArmed ? '' : undefined}
        onClick={slotTapArmed ? () => onSlotTap?.(index) : undefined}
        className={`mx-auto flex max-h-full items-center justify-center rounded-md border border-dashed bg-stucco1/30 font-data text-[10px] uppercase tracking-[0.3em] text-graphite transition-colors md:text-[11px] ${railBox} ${
          size === 'sm' ? 'aspect-square' : 'aspect-[3/4]'
        } ${
          isValidDropTarget
            ? 'border-sodium bg-sodium/10 text-sodium'
            : slotTapArmed
              ? 'animate-pulse border-sodium/80 bg-sodium/10 text-sodium'
              : ''
        }`}
        style={{
          borderColor: isValidDropTarget || slotTapArmed ? '#ff8a3d' : EDGES.mid,
          ...cardCap,
        }}
        role={slotTapArmed ? 'button' : undefined}
        aria-label={
          slotTapArmed ? `place in slot ${index + 1}` : `empty slot ${index + 1}`
        }
      >
        {index + 1}
      </div>
    );
  }

  const fxRing =
    fx.length > 0
      ? fx.some((e) => e.temp)
        ? 'rounded-md ring-2 ring-iceCrystal'
        : 'rounded-md ring-2 ring-glowStick'
      : '';

  return (
    <motion.div
      key={card.instanceId}
      initial={
        reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.7, y: -10 }
      }
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.22, ease: 'easeOut' }}
      className={`relative ${
        railed ? `h-full shrink-0 ${cardAspectClass(size)}` : ''
      } ${fxRing}`}
      style={cardCap}
    >
      <DraggableBoardCard
        card={card}
        index={index}
        size={size}
        railed={railed}
        isValidDropTarget={isValidDropTarget}
        setDropRef={setDropRef}
        targetingUnits={targetingUnits}
        onTargetSelect={onTargetSelect}
        selected={selected}
        onSelect={onSelect}
        moveArmed={moveArmed}
        onSlotTap={onSlotTap}
      />
      <AnimatePresence>
        {fx.map((e) => (
          <BuffFloater key={e.id} atk={e.atk} hp={e.hp} temp={e.temp} />
        ))}
      </AnimatePresence>
    </motion.div>
  );
});

const BuffFloater = React.memo(function BuffFloater({
  atk,
  hp,
  temp,
}: {
  atk: number;
  hp: number;
  temp: boolean;
}) {
  const sign = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
  return (
    <motion.span
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: -18 }}
      exit={{ opacity: 0, y: -28 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={`pointer-events-none absolute -top-[6px] left-1/2 z-30 -translate-x-1/2 whitespace-nowrap font-data text-[12px] font-bold md:text-[14px] ${
        temp ? 'text-iceCrystal' : 'text-glowStick'
      }`}
      style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
      aria-hidden
    >
      {sign(atk)}/{sign(hp)}
    </motion.span>
  );
});

const ChipFloater = React.memo(function ChipFloater({
  text,
  toneClass,
}: {
  text: string;
  toneClass: string;
}) {
  return (
    <motion.span
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: -22 }}
      exit={{ opacity: 0, y: -32 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={`pointer-events-none absolute -top-[2px] left-0 z-30 font-data text-[12px] font-bold md:text-[14px] ${toneClass}`}
      style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
      aria-hidden
    >
      {text}
    </motion.span>
  );
});

const DraggableBoardCard = React.memo(function DraggableBoardCard({
  card,
  index,
  size,
  railed = false,
  isValidDropTarget,
  setDropRef,
  targetingUnits,
  onTargetSelect,
  selected = false,
  onSelect,
  moveArmed = false,
  onSlotTap,
}: {
  card: Card;
  index: number;
  size: CardSize;
  railed?: boolean;
  isValidDropTarget: boolean;
  setDropRef: (node: HTMLElement | null) => void;
  targetingUnits: boolean;
  onTargetSelect?: (index: number) => void;
  selected?: boolean;
  onSelect?: (index: number) => void;
  moveArmed?: boolean;
  onSlotTap?: (index: number) => void;
}) {
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } =
    useDraggable({
      id: `board:${index}`,
      disabled: targetingUnits,
      data: { kind: 'board', index },
    });

  const combinedRef = useCallback((node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  }, [setDragRef, setDropRef]);

  const liveListeners = targetingUnits ? undefined : listeners;
  const liveAttributes = targetingUnits ? undefined : attributes;

  return (
    <div
      ref={combinedRef}
      data-selkeep
      style={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.2 : 1,
        touchAction: targetingUnits ? 'auto' : railed ? 'pan-x' : 'none',
      }}
      className={`${railed ? 'h-full' : ''} ${
        isValidDropTarget
          ? 'rounded-md ring-2 ring-sodium transition-shadow'
          : 'transition-shadow'
      } ${
        targetingUnits || moveArmed
          ? 'rounded-md ring-2 ring-sodium shadow-sodium animate-pulse'
          : ''
      }`}
      {...liveAttributes}
      {...liveListeners}
    >
      <CardTile
        card={card}
        size={size}
        fill={railed}
        selected={selected}
        onClick={
          targetingUnits
            ? () => onTargetSelect?.(index)
            : moveArmed
              ? () => onSlotTap?.(index)
              : () => onSelect?.(index)
        }
      />
    </div>
  );
});

const SellStrip = React.memo(function SellStrip({ visible }: { visible: boolean }) {
  const { isOver, setNodeRef } = useDroppable({
    id: 'sell-strip',
    data: { kind: 'sell-strip' },
  });
  return (
    <div
      ref={setNodeRef}
      aria-hidden={!visible}
      className={`pb-safe fixed inset-x-0 bottom-0 z-20 flex min-h-[88px] items-center justify-center border-t-2 px-md transition-all md:min-h-[96px] ${
        visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'
      } ${isOver ? 'bg-aerosolRed/35' : 'bg-aerosolRed/20'}`}
      style={{ borderColor: '#e6342a' }}
      role="region"
      aria-label="drop to sell"
    >
      <span
        className={`font-data text-[12px] uppercase tracking-[0.3em] md:text-[14px] ${
          isOver ? 'text-bone' : 'text-aerosolRed'
        }`}
      >
        drop to sell · +1 gold
      </span>
    </div>
  );
});

const HeroPowerRailButton = React.memo(function HeroPowerRailButton({
  label,
  description,
  state,
  available,
  isActive,
  cost,
  canAfford,
  usedThisTurn,
  onActivate,
  onCancel,
  reducedMotion,
  variant = 'column',
}: {
  label: string;
  description?: string;
  state: 'idle' | 'targeting' | 'discovering' | 'used';
  available: boolean;
  isActive: boolean;
  cost: number;
  canAfford: boolean;
  usedThisTurn: boolean;
  onActivate: () => void;
  onCancel: () => void;
  reducedMotion: boolean;
  variant?: 'column' | 'dock';
}) {
  const controls = useAnimationControls();
  const prevStateRef = useRef(state);
  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = state;
    if (reducedMotion) return;
    if (prev === 'idle' && state !== 'idle') {
      controls.start({
        x: [0, -4, 4, -3, 3, -2, 2, 0],
        transition: { duration: 0.35, ease: 'easeOut' },
      });
    }
  }, [state, controls, reducedMotion]);

  let subtitle: string;
  let disabled: boolean;
  let handler: (() => void) | undefined;
  let tone: 'sodium' | 'graphite' | 'tecate' = 'sodium';
  let activeRing = false;
  let primed = false;
  let passive = false;
  let icon: string;

  if (!available) {
    subtitle = 'loading';
    disabled = true;
    handler = undefined;
    tone = 'graphite';
    icon = '◌';
  } else if (state === 'targeting' || state === 'discovering') {
    subtitle = 'cancel';
    disabled = false;
    handler = onCancel;
    tone = 'sodium';
    activeRing = true;
    icon = '✕';
  } else if (!isActive) {
    subtitle = 'always on';
    disabled = true;
    handler = undefined;
    tone = 'tecate';
    passive = true;
    icon = '◇';
  } else if (state === 'used' || usedThisTurn) {
    subtitle = 'used';
    disabled = true;
    handler = undefined;
    tone = 'graphite';
    icon = '◆';
  } else if (!canAfford) {
    subtitle = `${cost}g`;
    disabled = true;
    handler = undefined;
    tone = 'graphite';
    icon = '⚡';
  } else {
    subtitle = cost > 0 ? `${cost}g` : 'free';
    disabled = false;
    handler = onActivate;
    tone = 'sodium';
    primed = true;
    icon = '⚡';
  }

  const toneText =
    tone === 'sodium' ? 'text-sodium' : tone === 'tecate' ? 'text-tecate' : 'text-graphite';
  const bgClass = primed
    ? 'bg-sodium/15'
    : passive
      ? 'bg-tecate/10'
      : tone === 'sodium'
        ? 'bg-stucco2'
        : 'bg-stucco1';

  const stateText = !available
    ? 'loading'
    : state === 'targeting'
      ? 'pick a target'
      : state === 'discovering'
        ? 'choose a reward'
        : passive
          ? 'always on'
          : usedThisTurn || state === 'used'
            ? 'used this turn'
            : !canAfford
              ? `needs ${cost}g`
              : cost > 0
                ? `ready · ${cost}g`
                : 'ready';

  const cadence = passive
    ? 'passive · always on'
    : cost > 0
      ? `${cost}g · once per turn`
      : 'free · once per turn';
  const explainDetail = `${stateText} · ${cadence}`;

  if (variant === 'dock') {
    return (
      <Explain
        title={label}
        body={description}
        detail={explainDetail}
        side="top"
        touch="longpress"
        className="flex shrink-0"
      >
        <motion.button
          onClick={handler}
          disabled={disabled}
          animate={controls}
          aria-label={description ? `${label}: ${description}` : label}
          className={`flex min-h-[48px] w-[46px] shrink-0 flex-col items-center justify-center gap-[2px] rounded-sm border transition-colors ${bgClass} ${
            disabled
              ? passive
                ? 'cursor-default'
                : 'cursor-not-allowed opacity-50'
              : 'hover:bg-stucco3'
          } ${activeRing ? 'ring-1 ring-sodium' : ''} ${primed ? 'shadow-sodiumSoft' : ''}`}
          style={{
            borderColor: primed
              ? 'rgba(255,138,61,0.4)'
              : passive
                ? 'rgba(214,99,53,0.35)'
                : EDGES.faint,
          }}
        >
          <span
            className={`font-data text-[15px] leading-none ${toneText} ${
              primed ? 'drop-shadow-[0_0_4px_rgba(255,138,61,0.7)]' : ''
            }`}
            aria-hidden
          >
            {icon}
          </span>
          <span className="font-data text-[10px] leading-none text-graphite" aria-hidden>
            {subtitle}
          </span>
        </motion.button>
      </Explain>
    );
  }

  return (
    <Explain
      title={label}
      body={description}
      detail={explainDetail}
      side="left"
      touch="longpress"
      className="flex w-full"
    >
      <motion.button
        onClick={handler}
        disabled={disabled}
        aria-label={description ? `${label}: ${description}` : label}
        animate={controls}
        className={`flex w-full min-h-[44px] items-center justify-between gap-xs rounded-sm border px-xs py-[2px] transition-colors sm:gap-sm sm:px-sm sm:py-xs md:min-h-[52px] md:px-md md:py-sm ${bgClass} ${
          disabled
            ? passive
              ? 'cursor-default'
              : 'cursor-not-allowed opacity-50'
            : 'hover:bg-stucco3'
        } ${activeRing ? 'ring-1 ring-sodium' : ''} ${
          primed ? 'shadow-sodiumSoft' : ''
        }`}
        style={{
          borderColor: primed
            ? 'rgba(255,138,61,0.4)'
            : passive
              ? 'rgba(214,99,53,0.35)'
              : EDGES.faint,
        }}
      >
        <span className="flex min-w-0 flex-1 items-center gap-xs md:gap-sm">
          <span
            className={`shrink-0 font-data text-[12px] leading-none sm:text-[14px] ${toneText} ${
              primed ? 'drop-shadow-[0_0_4px_rgba(255,138,61,0.7)]' : ''
            }`}
            aria-hidden
          >
            {icon}
          </span>
          <span
            className={`min-w-0 flex-1 truncate font-data text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.2em] md:text-[13px] lg:text-[14px] ${toneText}`}
          >
            {label}
          </span>
        </span>
        <span
          className={`shrink-0 font-data text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] md:text-[11px] lg:text-[12px] ${
            primed ? 'text-sodium' : passive ? 'text-tecate' : 'text-graphite'
          }`}
        >
          {subtitle}
        </span>
      </motion.button>
    </Explain>
  );
});

const RailButton = React.memo(function RailButton({
  onClick,
  disabled,
  label,
  cost,
  tone,
  active,
  explain,
  detail,
  icon,
  variant = 'column',
}: {
  onClick?: () => void;
  disabled?: boolean;
  label: string;
  cost: string;
  tone: 'sodium' | 'tecate' | 'ice' | 'graphite';
  active?: boolean;
  explain: ExplainId;
  detail?: string;
  icon: string;
  variant?: 'column' | 'dock';
}) {
  const toneText =
    tone === 'sodium'
      ? 'text-sodium'
      : tone === 'tecate'
        ? 'text-tecate'
        : tone === 'ice'
          ? 'text-iceCrystal'
          : 'text-graphite';

  if (variant === 'dock') {
    return (
      <Explain
        id={explain}
        detail={detail ? `${cost} · ${detail}` : cost}
        side="top"
        touch="longpress"
        className="flex shrink-0"
      >
        <button
          onClick={onClick}
          disabled={disabled}
          aria-label={`${label}, ${cost}`}
          className={`flex min-h-[48px] w-[46px] shrink-0 flex-col items-center justify-center gap-[2px] rounded-sm border bg-stucco2 transition-colors ${
            disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-stucco3'
          } ${active ? 'ring-1 ring-iceBorder' : ''}`}
          style={{ borderColor: EDGES.faint }}
        >
          <span className={`font-data text-[15px] leading-none ${toneText}`} aria-hidden>
            {icon}
          </span>
          <span className="font-data text-[10px] leading-none text-graphite" aria-hidden>
            {cost}
          </span>
        </button>
      </Explain>
    );
  }

  return (
    <Explain
      id={explain}
      detail={detail}
      side="left"
      touch="longpress"
      className="flex w-full"
    >
      <button
        onClick={onClick}
        disabled={disabled}
        className={`group flex min-h-[44px] w-full items-center justify-between gap-xs rounded-sm border bg-stucco2 px-xs py-[2px] transition-colors sm:gap-sm sm:px-sm sm:py-xs md:min-h-[52px] md:px-md md:py-sm ${
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-stucco3'
        } ${active ? 'ring-1 ring-iceBorder' : ''}`}
        style={{ borderColor: EDGES.faint }}
      >
        <span
          className={`font-data text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.2em] md:text-[13px] lg:text-[14px] ${toneText}`}
        >
          {label}
        </span>
        <span className="font-data text-[10px] uppercase tracking-[0.15em] text-graphite sm:tracking-[0.2em] md:text-[11px] lg:text-[12px]">
          {cost}
        </span>
      </button>
    </Explain>
  );
});
